import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { faChevronDown, faChevronUp, faEdit, faCopy } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNotification } from "../../context/NotificationContext";
import { useAppContext } from "../../context/AppContext";
import { loadMyTickets } from "../../api/ticketsApi";
import Loading from "../../components/Loading";
import { pageSize } from "../util/constants";

const MyTicketList = ({ searchParams }) => {
    const navigate = useNavigate();
    const notify = useNotification();
    const { updateTicketId, setUpdateTicketId } = useAppContext();

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [sortField, setSortField] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState("desc");
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const [expandedRows, setExpandedRows] = useState({});

    const fetchTickets = async () => {
        try {
            const params = {
                page,
                pageSize,
                sortField,
                sortOrder,
                searchText: searchParams.searchText ?? null,
                createdByText: searchParams.createdByText ?? null,
                searchType: searchParams.searchType ?? null,
                defaultSearchFields: searchParams.defaultSearchFields ?? null,
                isCreatedBy: false
            };
            setLoading(true);
            const response = await loadMyTickets(params);
            if (response.status === 200) {
                setTickets(response.data.content);
                setTotalPages(response.data.totalPages);
                setTotalElements(response.data.totalElements);
            }
        } catch (error) {
            notify("error", "Failed to load tickets");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, [page, searchParams, sortOrder]);

    const handlePageChange = (newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setPage(newPage);
        }
    };

    const handleEdit = (ticketId) => {
        window.open(`/tickets/update?tid=${ticketId}`, '_blank');
    };

    const handleSort = (field) => {
        const isAscending = sortField === field && sortOrder === "asc";
        setSortField(field);
        setSortOrder(isAscending ? "desc" : "asc");
    };

    const renderSortIcon = (field) => {
        if (sortField === field) {
            return (
                <span className="ml-1 text-blue-500 cursor-pointer">
                    <FontAwesomeIcon icon={sortOrder === "asc" ? faChevronUp : faChevronDown} />
                </span>
            );
        }
        return <span className="ml-1 opacity-50 cursor-pointer"><FontAwesomeIcon icon={faChevronUp} /></span>;
    };

    const toggleRow = (ticketId) => {
        setExpandedRows((prev) => ({
            ...prev,
            [ticketId]: !prev[ticketId],
        }));
    };

    const handleCopyUrl = (ticketId) => {
        const url = `${window.location.origin}/tickets/update?tid=${ticketId}`;
        navigator.clipboard.writeText(url)
            .then(() => {
            })
            .catch((error) => {
                notify("error", "Failed to copy URL.");
            });
    };

    if (loading) {
        return Loading();
    }

    const tdProp = {
        className: "border px-1 py-1 text-center",
    };

    return (
        <>
            <div>
                <table className="table-auto w-full text-left border-collapse">
                    <thead>
                        <tr>
                            <th {...tdProp}>Token</th>
                            <th {...tdProp}>Customer</th>
                            <th {...tdProp}>Contact</th>
                            <th {...tdProp}>Head</th>
                            <th {...tdProp}>Category</th>
                            <th {...tdProp}>Status</th>
                            <th {...tdProp}>Priority</th>
                            <th {...tdProp}>Assignee</th>
                            <th {...tdProp} onClick={() => handleSort('dueDate')}>Due Date {renderSortIcon("dueDate")}</th>
                            <th {...tdProp} onClick={() => handleSort('createdAt')} >
                                Created At {renderSortIcon("createdAt")}
                            </th>
                            <th {...tdProp}>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tickets.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="text-center py-4">No tickets available.</td>
                            </tr>
                        ) : (
                            tickets.map((ticket) => (
                                <React.Fragment key={ticket.id}>
                                    <tr>
                                        <td {...tdProp}>{ticket.id}</td>
                                        <td {...tdProp}>{ticket.customerJson?.username || 'N/A'}</td>
                                        <td {...tdProp}>{ticket.customerJson?.cell || 'N/A'}</td>
                                        <td {...tdProp}>{ticket.problemHeadName}</td>
                                        <td {...tdProp}>{ticket.problemCategoryName}</td>
                                        <td {...tdProp}>{ticket.progressStatusName}</td>
                                        <td {...tdProp}>{ticket.priorityName}</td>
                                        <td {...tdProp}>{ticket.assignee ?? "-"}</td>
                                        <td {...tdProp}>{ticket.dueDate ?? "-"}</td>
                                        <td {...tdProp}>{ticket.createdAt ?? "-"}</td>
                                        <td {...tdProp}>
                                            <div className="flex space-x-2">
                                                <button onClick={() => toggleRow(ticket.id)} className="text-blue-500 hover:text-blue-700">
                                                    <FontAwesomeIcon icon={expandedRows[ticket.id] ? faChevronUp : faChevronDown} className="text-lg" />
                                                </button>
                                                <button
                                                    onClick={() => handleEdit(ticket.id)}
                                                    className="text-blue-500 hover:text-blue-700"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} className="text-lg" />
                                                </button>
                                                <button
                                                    onClick={() => handleCopyUrl(ticket.id)}
                                                    className="text-blue-300 hover:text-blue-500"
                                                    title="Copy URL"
                                                >
                                                    <FontAwesomeIcon icon={faCopy} className="text-lg" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    {expandedRows[ticket.id] && (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-2 bg-gray-50">
                                                {/* Ticket Details Table */}
                                                <div className="mb-4">
                                                    <strong>Ticket Details:</strong>
                                                    <table className="w-full border-collapse mt-2">
                                                        <tbody>
                                                            {Object.entries(ticket.ticketJson || {}).map(([key, value]) => (
                                                                <tr key={key}>
                                                                    <th {...tdProp}>{key}</th>
                                                                    <td {...tdProp}>{value?.toString() || 'N/A'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                {/* Customer Details Table */}
                                                <div>
                                                    <strong>Customer Details:</strong>
                                                    <table className="w-full border-collapse mt-2">
                                                        <tbody>
                                                            {Object.entries(ticket.customerJson || {}).map(([key, value]) => (
                                                                <tr key={key}>
                                                                    <th {...tdProp}>{key}</th>
                                                                    <td {...tdProp}>{value?.toString() || 'N/A'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
                <div className="flex justify-between items-center mt-4">
                    <button
                        disabled={page === 0}
                        onClick={() => handlePageChange(page - 1)}
                        className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold rounded disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span>
                        Page {page + 1} of {totalPages}
                    </span>
                    <button
                        disabled={page === totalPages - 1 || totalPages === 0}
                        onClick={() => handlePageChange(page + 1)}
                        className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold rounded disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            </div>
        </>
    );
};

export default MyTicketList;

